import { CommonModule } from "@angular/common";
import { Component, computed, ContentChildren, effect, inject, input, output, QueryList, signal, TemplateRef } from "@angular/core";
import { SplitNavContentDirective } from "../../core/directives/split-nav-content.directive";
import { NavItem } from "../../core/models/ui/profile-nav-item.model";
import { AuthorizationService } from "../../core/services/authorization/authorization.service";
import { AuthorizationStateService } from "../../core/services/authorization/authorization-state.service";
import { UserSessionService } from "../../core/services/user-session.service";
import { ProfileOrganizationScopeService } from "../../core/services/authorization/profile-organization-scope.service";
import { PermissionUtilities } from "../../core/utilities/permission.utilities";
import { ResponsiveTabsComponent } from "../responsive-tabs/responsive-tabs.component";

@Component({
  selector: 'ms-split-navigation-panel',
  standalone: true,
  imports: [CommonModule, ResponsiveTabsComponent],
  templateUrl: './split-navigation-panel.component.html',
  styleUrls: ['./split-navigation-panel.component.scss'],
})
export class SplitNavigationPanelComponent {
  @ContentChildren(SplitNavContentDirective) contentTemplates!: QueryList<SplitNavContentDirective>;

  private readonly authorizationService = inject(AuthorizationService);
  private readonly authorizationStateService = inject(AuthorizationStateService);
  private readonly userSessionService = inject(UserSessionService);
  // Provided by an org-scoped profile page so the sub-nav is filtered against the organization on
  // screen rather than the active one; null when this panel is used elsewhere (user settings).
  private readonly profileScope = inject(ProfileOrganizationScopeService, { optional: true });

  public title = input<string>('Settings');
  public navItems = input.required<NavItem[]>();
  public initialTabId = input<string | undefined>(undefined);

  public tabChanged = output<string>();

  public activeTabId = signal<string>('');

  // Filter nav items based on user permissions
  public filteredNavItems = computed<NavItem[]>(() => {
    const items = this.navItems();
    const authState = this.authorizationStateService.authorizationState();

    // If no auth state, only show items without permissions
    if (!authState) {
      return items.filter(item => !item.permission || item.permission.length === 0);
    }

    const scope = PermissionUtilities.resolveOrganizationScope(undefined, this.profileScope, this.userSessionService);

    // Hold the sub-nav back until the organization is known. The constructor effect below latches the
    // initial tab off the first non-empty result, so handing it a provisional list would pin the
    // wrong tab (DEV-2643).
    if (scope.isPending) {
      return [];
    }

    return items.filter(item => {
      // If no permissions are required, show the item
      if (!item.permission || item.permission.length === 0) {
        return true;
      }

      // Check if user has at least one of the required permissions
      return item.permission.some(permission => 
        this.authorizationService.hasPermission(
          authState, 
          permission, 
          scope.organizationId
        )
      );
    });
  });

  // The overflow strip reads the selected tab off the items themselves; this panel tracks it in a
  // signal of its own, so the flag is stamped on here rather than carried by the constants.
  public tabNavItems = computed<NavItem[]>(() =>
    this.filteredNavItems().map(item => ({ ...item, active: this.isActive(item.id) }))
  );

  // Initializes the active tab once filteredNavItems is non-empty. Re-runs reactively
  // when permissions/auth state load asynchronously, so an initialTabId requested via
  // a permission-gated tab is still honored after auth arrives.
  private hasInitialized = false;

  constructor() {
    effect(() => {
      const filtered = this.filteredNavItems();
      if (this.hasInitialized || filtered.length === 0) return;

      const initialId = this.initialTabId();
      const matched = initialId ? filtered.find(item => item.id === initialId) : undefined;
      const activeItem = filtered.find(item => item.active);
      const selected = matched ?? activeItem ?? filtered[0];

      this.hasInitialized = true;
      this.onTabClick(selected.id);
    });
  }

  public onTabClick(tabId: string): void {
    this.activeTabId.set(tabId);
    this.tabChanged.emit(tabId);
  }

  public isActive(tabId: string): boolean {
    return this.activeTabId() === tabId;
  }

  public getTemplateForTab(tabId: string): TemplateRef<any> | undefined {
    const directive = this.contentTemplates?.find(d => d.tabId === tabId);
    return directive?.template || undefined;
  }

}
